import React, { useState, useCallback } from "react";
import {
  useSaveHeadroomSegmentationDataMutation,
  useSaveHeadroomDriverMetricsMutation,
  SegmentResponse,
  DriverMetricsResponse,
} from "graphql/types/graphql";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { enqueueSnackbar } from "notistack";

interface SaveResult {
  loading: boolean;
  error: string | null;
}

interface SaveDataHook {
  saveSegmentationData: (segmentData: SegmentResponse[]) => Promise<void>;
  saveDriverMetricsData: (
    editedPercentiles: EditedMetrics,
    driverMetricsData: DriverMetricsResponse[]
  ) => Promise<void>;
  segmentationResult: SaveResult;
  driverMetricsResult: SaveResult;
}

export interface EditedMetrics {
  [uuid: string]: {
    driver1Percentile?: number;
    driver2Percentile?: number;
    driver1Median?: number;
    driver2Median?: number;
  };
}

export const useHeadroomSaveData = (): SaveDataHook => {
  const { selectedProject } = useHeadroomContext();
  const [saveSegmentationDataMutation, { loading: segmentationLoading, error: segmentationMutationError }] =
    useSaveHeadroomSegmentationDataMutation();
  const [saveDriverMetricsMutation, { loading: driverMetricsLoading, error: driverMetricsMutationError }] =
    useSaveHeadroomDriverMetricsMutation();
  const { showError, clearError } = useErrorSnackbar();
  const [segmentationError, setSegmentationError] = useState<string | null>(null);

  const getFriendlyErrorMessage = (errorMessage: string): string => {
    if (errorMessage.includes("No matching data found to update for the following segments")) {
      return "The provided segment data does not match any existing records. Please verify the segment details and try again.";
    }
    return errorMessage;
  };

  const saveSegmentationData = useCallback(
    async (segmentData: SegmentResponse[]) => {
      if (!selectedProject) {
        setSegmentationError("Project name is required to save segmentation data.");
        showError("Project name is required to save segmentation data.");
        return;
      }
      if (!segmentData?.length) {
        setSegmentationError("No segmentation data available to save.");
        showError("No segmentation data available to save.");
        return;
      }
      try {
        const input = {
          projectName: selectedProject,
          edges: segmentData.map((edge) => ({
            segment: edge.segment,
            segmentValue: edge.segmentValue,
            segmentRange: edge.segmentRange || null,
            count1: edge.count1 || 0,
            count2: edge.count2 || 0,
            numerator: edge.numerator,
          })),
        };

        const result = await saveSegmentationDataMutation({
          variables: { input },
        });

        if (!result.data?.saveHeadroomSegmentationData.success) {
          const errorMessage =
            result.data?.saveHeadroomSegmentationData.errorMessage || "Failed to save segmentation data.";

          setSegmentationError(errorMessage);
          showError(errorMessage);
          return;
        }

        setSegmentationError(null);
        clearError();
        enqueueSnackbar(
          result.data?.saveHeadroomSegmentationData.isUpdate
            ? "Segmentation data updated successfully!"
            : "Segmentation data saved successfully!",
          { variant: "success" }
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);

        setSegmentationError(errorMessage);
        showError(errorMessage);
      }
    },
    [selectedProject, showError, saveSegmentationDataMutation, clearError]
  );

  const saveDriverMetricsData = useCallback(
    async (editedPercentiles: EditedMetrics, driverMetricsData: DriverMetricsResponse[]) => {
      if (!selectedProject) {
        showError("Project name is required to save driver metrics data.");
        return;
      }
      if (!driverMetricsData?.length) {
        showError("No driver metrics data available to save.");
        return;
      }
      try {
        const input = {
          projectName: selectedProject,
          edges: driverMetricsData.map((edge) => {
            const uuid = edge.FxUniqueRowUuid || `${edge.segmentValue}_${edge.segmentRange || "null"}`;
            return {
              segment: edge.segment,
              segmentValue: edge.segmentValue,
              segmentRange: edge.segmentRange || null,
              current: edge.driver1Median ? edge.driver1Median : edge.driver2Median,
              mean: edge.driver1Avg ? edge.driver1Avg : edge.driver2Avg,
              driver1Percentile: editedPercentiles[uuid]?.driver1Percentile ?? edge.driver1Percentile,
              driver2Percentile: editedPercentiles[uuid]?.driver2Percentile ?? edge.driver2Percentile,
              driver1Denominator: edge.driver1Denominator,
              driver2Denominator: edge.driver2Denominator,
            };
          }),
        };

        const result = await saveDriverMetricsMutation({
          variables: { input },
        });

        if (!result.data?.saveHeadroomDriverMetrics.success) {
          const errorMessage =
            result.data?.saveHeadroomDriverMetrics.errorMessage || "Failed to save driver metrics data.";
          const friendlyMessage = getFriendlyErrorMessage(errorMessage);
          showError(friendlyMessage);
          return;
        }

        clearError();
        enqueueSnackbar(
          result.data.saveHeadroomDriverMetrics.isUpdate
            ? "Driver metrics data updated successfully!"
            : "Driver metrics data saved successfully!",
          { variant: "success" }
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);

        showError(errorMessage);
      }
    },
    [selectedProject, showError, saveDriverMetricsMutation, clearError]
  );

  return {
    saveSegmentationData,
    saveDriverMetricsData,
    segmentationResult: {
      loading: segmentationLoading || driverMetricsLoading,
      error: segmentationError || segmentationMutationError?.message || null,
    },
    driverMetricsResult: {
      loading: driverMetricsLoading,
      error: driverMetricsMutationError?.message || null,
    },
  };
};
